import React, { useState, useEffect, useRef, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Camera, MapPin, RefreshCw, CheckCircle, Clock, AlertTriangle, 
  User, Compass, Calendar, ArrowRight, VideoOff 
} from "lucide-react";
import { employeeWorkforceAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import EmptyState from "../../components/EmptyState";

export default function AttendancePage() {
  const { addNotification } = useContext(NotificationContext);
  const [todayStatus, setTodayStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Camera & GPS State
  const [cameraActive, setCameraActive] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [gpsError, setGpsError] = useState("");
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const fetchStatusAndLogs = async () => {
    setLoading(true);
    try {
      const [statusRes, logsRes] = await Promise.all([
        employeeWorkforceAPI.getTodayAttendanceStatus(),
        employeeWorkforceAPI.getAttendanceLogs()
      ]);
      setTodayStatus(statusRes.data);
      setLogs(logsRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to fetch attendance data", "error");
    } finally {
      setLoading(false);
    }
  };

  // GPS Coordination & Reverse Geocoding
  const fetchGPSLocation = (silent = false) => {
    const fallbackCoords = { latitude: 12.9716, longitude: 77.5946 };
    const defaultOfficeAddress = "Office Location (Main Branch)";

    if (!navigator.geolocation) {
      setCoords(fallbackCoords);
      setAddress(defaultOfficeAddress);
      return;
    }

    if (!silent) {
      setGpsError("");
      setAddress("Fetching location details...");
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCoords({ latitude, longitude });
        
        // Reverse Geocoding using free OpenStreetMap API (Nominatim)
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          if (data && data.display_name) {
            setAddress(data.display_name);
          } else {
            setAddress(`Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`);
          }
        } catch (e) {
          setAddress(`Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`);
        }
      },
      (error) => {
        // Quietly apply office location fallback without logging noisy console errors
        setCoords(fallbackCoords);
        setAddress(defaultOfficeAddress);
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    fetchStatusAndLogs();
    fetchGPSLocation(true);
    return () => stopCamera();
  }, []);

  // Camera Management
  const startCamera = async () => {
    setSelfie(null);
    setCameraActive(true);
    fetchGPSLocation(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access failed", err);
      addNotification("Could not access camera. Please check permissions.", "error");
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = 480;
      canvas.height = 360;
      const ctx = canvas.getContext("2d");
      
      // Mirror the image for selfie natural look
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      const dataUrl = canvas.toDataURL("image/jpeg");
      setSelfie(dataUrl);
      stopCamera();
    }
  };


  // Submit attendance punch
  const handlePunch = async (type) => {
    if (!selfie) {
      addNotification("Please capture your selfie check-in photo first.", "warning");
      return;
    }
    if (!coords) {
      addNotification("Please retrieve your GPS coordinates.", "warning");
      return;
    }

    setActionLoading(true);
    try {
      if (type === "in") {
        const payload = {
          punch_in_photo: selfie,
          punch_in_latitude: coords.latitude,
          punch_in_longitude: coords.longitude,
          punch_in_address: address
        };
        await employeeWorkforceAPI.checkIn(payload);
        addNotification("Checked In successfully!", "success");
      } else {
        const payload = {
          punch_out_photo: selfie,
          punch_out_latitude: coords.latitude,
          punch_out_longitude: coords.longitude,
          punch_out_address: address
        };
        await employeeWorkforceAPI.checkOut(payload);
        addNotification("Checked Out successfully!", "success");
      }
      // Reset state and reload records
      setSelfie(null);
      setCoords(null);
      setAddress("");
      fetchStatusAndLogs();
    } catch (err) {
      console.error(err);
      addNotification(err.response?.data?.detail || "Attendance request failed.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="verification"
          title="Verifying Attendance Records..."
          message="Please wait a moment."
        />
      </div>
    );
  }

  const attendanceColors = {
    "ON TIME": "bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
    "LATE": "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "FIELD VISIT": "bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800",
    "HALF DAY": "bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800",
    "ABSENT": "bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800",
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">Daily Attendance Punch</h1>
              <p className="text-gray-500 dark:text-slate-400 font-medium">Verify your day with selfie & GPS location tracking</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Action Punch Panel */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm space-y-6">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
              {todayStatus?.checked_in ? (todayStatus?.checked_out ? "Punch Complete" : "Punch Out (End Day)") : "Punch In (Start Day)"}
            </h2>

            {todayStatus?.checked_in && todayStatus?.checked_out ? (
              <div className="p-8 text-center bg-green-50 dark:bg-green-950/20 rounded-2xl border border-green-200 dark:border-green-800/30 space-y-3">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
                <p className="text-lg font-black text-gray-900 dark:text-white">Attendance Recorded!</p>
                <p className="text-sm text-gray-500 dark:text-slate-400 font-medium">
                  You have successfully checked in and out for today. See logs below.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                
                {/* Geolocation Section */}
                <div className="bg-gray-50 dark:bg-slate-900 p-5 rounded-2xl border border-gray-100 dark:border-slate-800 space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-black text-gray-700 dark:text-slate-300 flex items-center gap-2">
                      <Compass className="w-4 h-4 text-green-600" />
                      GPS Coordinates
                    </p>
                    <button
                      onClick={fetchGPSLocation}
                      className="text-xs font-bold text-green-600 hover:text-green-700 flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-3 py-1.5 rounded-xl shadow-xs"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Get GPS Location
                    </button>
                  </div>

                  {coords ? (
                    <div className="space-y-2">
                      <div className="flex gap-4 text-xs font-bold text-gray-600 dark:text-slate-400">
                        <span>Lat: {coords.latitude.toFixed(6)}</span>
                        <span>Lon: {coords.longitude.toFixed(6)}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-400 font-medium line-clamp-2">
                        Address: {address}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      {gpsError ? (
                        <p className="text-xs text-red-500 font-semibold">{gpsError}</p>
                      ) : (
                        <p className="text-xs text-gray-400 font-medium">No GPS coordinates locked yet. Click button above.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Camera / Selfie Capture */}
                <div className="space-y-3">
                  <p className="text-sm font-black text-gray-700 dark:text-slate-300 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-green-600" />
                    Selfie Verification
                  </p>

                  <div className="w-full h-64 bg-slate-100 dark:bg-slate-900 rounded-2xl overflow-hidden relative border border-gray-200 dark:border-slate-800 flex items-center justify-center">
                    <AnimatePresence mode="wait">
                      {cameraActive ? (
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          className="w-full h-full object-cover scale-x-[-1]"
                        />
                      ) : selfie ? (
                        <img src={selfie} alt="Check-in selfie" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center p-6 text-gray-400 dark:text-slate-500">
                          <VideoOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-xs font-medium">Camera is offline. Click button below to start.</p>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="flex gap-3">
                    {cameraActive ? (
                      <button
                        onClick={capturePhoto}
                        className="flex-1 py-3 bg-green-600 text-white rounded-2xl font-bold hover:bg-green-700 transition active:scale-95 text-sm shadow-lg shadow-green-500/10"
                      >
                        Capture Selfie
                      </button>
                    ) : (
                      <button
                        onClick={startCamera}
                        className="flex-1 py-3 bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-slate-600 rounded-2xl font-bold transition active:scale-95 text-sm border border-gray-200 dark:border-slate-600"
                      >
                        {selfie ? "Retake Photo" : "Start Selfie Camera"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
                  <button
                    onClick={() => handlePunch(todayStatus?.checked_in ? "out" : "in")}
                    disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-slate-800 rounded-2xl font-bold transition active:scale-95 text-md shadow-lg shadow-green-500/20"
                  >
                    {actionLoading && <RefreshCw className="w-5 h-5 animate-spin" />}
                    Confirm Punch {todayStatus?.checked_in ? "Out" : "In"}
                  </button>
                </div>

              </div>
            )}
          </div>

          {/* Today Overview Panel */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider mb-6">
                Punch Overview
              </h2>

              <div className="space-y-6">
                <TimelineNode 
                  label="Morning Check-In" 
                  time={todayStatus?.record?.punch_in_time} 
                  photo={todayStatus?.record?.punch_in_photo} 
                  address={todayStatus?.record?.punch_in_address} 
                  active={!!todayStatus?.checked_in}
                />
                
                <div className="w-0.5 h-12 bg-gray-100 dark:bg-slate-700 ml-6" />

                <TimelineNode 
                  label="Evening Check-Out" 
                  time={todayStatus?.record?.punch_out_time} 
                  photo={todayStatus?.record?.punch_out_photo} 
                  address={todayStatus?.record?.punch_out_address} 
                  active={!!todayStatus?.checked_out}
                />
              </div>
            </div>

            {todayStatus?.record?.total_hours && (
              <div className="mt-8 p-4 bg-gray-50 dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 flex justify-between items-center">
                <span className="text-sm text-gray-400 font-bold uppercase tracking-wider">Total Working Hours</span>
                <span className="text-lg font-black text-green-600 dark:text-green-400">{todayStatus.record.total_hours} Hours</span>
              </div>
            )}
          </div>

        </div>

        {/* Previous Attendance Logs */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-gray-50 dark:border-slate-700">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
              Attendance Log History
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Date</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Check In</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Check Out</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Total Hours</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Punch Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                      No past attendance records found.
                    </td>
                  </tr>
                ) : logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                      {new Date(log.date).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400">
                      {log.punch_in_time ? new Date(log.punch_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400">
                      {log.punch_out_time ? new Date(log.punch_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-white">
                      {log.total_hours ? `${log.total_hours} hrs` : "N/A"}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${attendanceColors[log.status] || "bg-gray-100 text-gray-700"}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-400 font-medium max-w-[200px] truncate" title={log.punch_in_address}>
                      {log.punch_in_address || "Unknown"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

function TimelineNode({ label, time, photo, address, active }) {
  return (
    <div className={`flex gap-4 ${active ? "opacity-100" : "opacity-40"}`}>
      <div className={`w-12 h-12 rounded-2xl flex-shrink-0 flex items-center justify-center ${active ? "bg-green-500 text-white" : "bg-gray-100 text-gray-400 dark:bg-slate-800"}`}>
        {photo ? (
          <img src={photo} alt={label} className="w-full h-full object-cover rounded-2xl" />
        ) : (
          <User className="w-5 h-5" />
        )}
      </div>
      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-400 font-medium mt-1 uppercase tracking-wider">
          {time ? new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "Not clocked yet"}
        </p>
        {address && (
          <p className="text-[10px] text-gray-500 dark:text-slate-400 line-clamp-2 max-w-[300px] mt-1 font-medium">
            📍 {address}
          </p>
        )}
      </div>
    </div>
  );
}
